import { useEffect, useRef, useState } from "react";

/**
 * INTERSECTION OBSERVER HOOK
 * Detects when element is in viewport
 * Useful for lazy loading, analytics, infinite scroll
 *
 * @param {Object} options - IntersectionObserver options
 * @param {number} options.threshold - Percentage of element visible (0-1)
 * @param {string} options.rootMargin - Margin around root
 * @param {boolean} options.triggerOnce - Only trigger once when element becomes visible
 * @returns {Object} { elementRef, isVisible, hasBeenVisible }
 */
export const useIntersectionObserver = (options = {}) => {
  const elementRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const observerRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // If triggerOnce is true and already been visible, don't observe
    if (options.triggerOnce && hasBeenVisible) return;

    const observerOptions = {
      threshold: options.threshold !== undefined ? options.threshold : 0.7, // 70% visible by default
      rootMargin: options.rootMargin || "0px",
      root: options.root || null,
    };

    observerRef.current = new IntersectionObserver(([entry]) => {
      const visible = entry.isIntersecting;
      setIsVisible(visible);

      // Track if element has ever been visible
      if (visible && !hasBeenVisible) {
        setHasBeenVisible(true);
      }

      // If triggerOnce, disconnect after first trigger
      if (options.triggerOnce && visible) {
        observerRef.current?.disconnect();
      }
    }, observerOptions);

    observerRef.current.observe(element);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    options.threshold,
    options.rootMargin,
    options.triggerOnce,
    hasBeenVisible,
    options.root,
  ]);

  return {
    elementRef,
    isVisible,
    hasBeenVisible,
  };
};

export default useIntersectionObserver;
